import { type ReactNode, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronDown, Mail } from 'lucide-react';
import { PageShell } from '@/components/page/PageKit';
import { cn } from '@/lib/utils';

export type LegalSection = { id: string; label: string };

const LEGAL_PAGES = [
  { label: 'Privacy Policy', href: '/privacy-policy' },
  { label: 'Terms of Service', href: '/terms-of-service' },
  { label: 'Security', href: '/security' },
];

/**
 * Shared layout for the legal pages: one hero, a sticky contents list and a single
 * typographic system for the text (the `.bv-legal` styles in index.css).
 * Sections are passed as children: <section id="…"><h2>…</h2>…</section>.
 */
export function LegalLayout({ title, updated, summary, current, sections, contact, children }: {
  title: ReactNode;
  updated: string;
  summary: string;
  current: string;
  sections: LegalSection[];
  contact: { label: string; email: string };
  children: ReactNode;
}) {
  const [active, setActive] = useState('');

  // Highlight the section being read. Runs after mount, so server and client first renders match.
  useEffect(() => {
    const els = sections.map(s => document.getElementById(s.id)).filter((el): el is HTMLElement => Boolean(el));
    const observer = new IntersectionObserver(entries => {
      const visible = entries.filter(e => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (visible[0]) setActive(visible[0].target.id);
    }, { rootMargin: '-96px 0px -65% 0px' });
    els.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [sections]);

  const tocList = (
    <ol className="space-y-0.5">
      {sections.map((s, i) => (
        <li key={s.id}>
          <a href={`#${s.id}`} className={cn('flex gap-3 rounded-lg px-3 py-1.5 text-[14px] leading-5 transition-colors', active === s.id ? 'bg-bv-blue-wash font-semibold text-bv-ink' : 'text-bv-ink2 hover:bg-bv-blue-wash/60 hover:text-bv-ink')}>
            <span className="w-5 shrink-0 text-right tabular-nums text-bv-mute">{i + 1}</span>
            <span>{s.label}</span>
          </a>
        </li>
      ))}
    </ol>
  );

  return (
    <PageShell reviewed={false}>
      <section className="relative overflow-hidden border-b border-bv-blue-line bg-bv-base pb-12 pt-32 sm:pt-36 lg:pb-14 lg:pt-40">
        <div aria-hidden className="bv-hero-grid pointer-events-none absolute inset-0" />
        <div className="relative mx-auto max-w-[1120px] px-5 lg:px-8">
          <span className="inline-flex items-center gap-2 rounded-full border border-bv-blue-line bg-white/80 px-3 py-1 text-[13px] font-semibold text-bv-blue"><span className="h-1.5 w-1.5 rounded-full bg-current" />Legal</span>
          <h1 className="mt-5 font-display text-[40px] font-semibold leading-[1.04] tracking-[-0.04em] text-bv-ink sm:text-[56px]">{title}</h1>
          <p className="mt-5 max-w-[640px] text-[17px] leading-[1.7] text-bv-ink2">{summary}</p>
          <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-3">
            <p className="text-[14px] font-medium text-bv-mute">Last updated <time className="text-bv-ink">{updated}</time></p>
            <nav aria-label="Legal pages" className="flex flex-wrap gap-2">
              {LEGAL_PAGES.map(page => (
                <Link key={page.href} to={page.href} aria-current={page.href === current ? 'page' : undefined} className={cn('rounded-full border px-3 py-1 text-[13px] font-semibold transition-colors', page.href === current ? 'border-bv-ink bg-bv-ink text-white' : 'border-bv-blue-line bg-white text-bv-ink2 hover:border-bv-blue/40 hover:text-bv-ink')}>{page.label}</Link>
              ))}
            </nav>
          </div>
        </div>
      </section>

      <div className="bg-white">
        <div className="mx-auto grid max-w-[1120px] grid-cols-1 gap-10 px-5 pb-20 pt-10 lg:grid-cols-[250px_minmax(0,1fr)] lg:gap-16 lg:px-8 lg:pb-28 lg:pt-14">
          <aside className="lg:sticky lg:top-24 lg:self-start">
            <details className="group rounded-2xl border border-bv-blue-line bg-bv-base p-2 lg:hidden">
              <summary className="flex cursor-pointer list-none items-center justify-between rounded-xl px-3 py-2 text-[14px] font-semibold text-bv-ink [&::-webkit-details-marker]:hidden">
                Contents <ChevronDown className="h-4 w-4 transition-transform group-open:rotate-180" />
              </summary>
              <div className="mt-1 pb-1">{tocList}</div>
            </details>
            <nav aria-label="On this page" className="hidden lg:block">
              <p className="mb-3 px-3 text-[12px] font-semibold uppercase tracking-[0.08em] text-bv-mute">On this page</p>
              {tocList}
            </nav>
          </aside>

          <div className="min-w-0">
            <article className="bv-legal">{children}</article>

            <div className="mt-14 flex flex-col gap-5 rounded-2xl border border-bv-blue-line bg-bv-base p-6 sm:flex-row sm:items-center sm:justify-between sm:p-7">
              <div className="flex gap-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-bv-blue shadow-bv-card"><Mail className="h-5 w-5" /></span>
                <div>
                  <p className="font-display text-lg font-semibold text-bv-ink">{contact.label}</p>
                  <a href={`mailto:${contact.email}`} className="text-[15px] font-medium text-bv-blue hover:underline">{contact.email}</a>
                </div>
              </div>
              <div className="flex flex-wrap gap-x-5 gap-y-2">
                {LEGAL_PAGES.filter(page => page.href !== current).map(page => (
                  <Link key={page.href} to={page.href} className="group inline-flex items-center gap-1 text-[14px] font-semibold text-bv-ink hover:text-bv-blue">{page.label}<ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" /></Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageShell>
  );
}
